import * as React from 'react'

import { cn } from '@/lib/utils'

type TableProps = React.ComponentProps<'table'> & {
  wrapRef?: React.Ref<HTMLDivElement>
  wrapClassName?: string
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, wrapRef, wrapClassName, ...props },
  ref,
) {
  return (
    <div
      data-slot="table-container"
      className={cn('relative w-full overflow-x-auto', wrapClassName)}
      ref={wrapRef}
    >
      <table data-slot="table" ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
})

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead data-slot="table-header" className={cn('border-border bg-muted-foreground-3 [&_tr]:border-b', className)} {...props} />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted-foreground-3 border-border border-t font-normal [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-muted-foreground-3 data-[state=selected]:bg-muted-foreground-3 border-border border-b transition-colors',
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-foreground h-14 px-4 text-left align-middle font-normal whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5',
        className,
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'py-3 px-4 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-0.5',
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption data-slot="table-caption" className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
